import React, { useRef, useState, useEffect, useContext } from "react"

import { TextElementNova } from "@nutrafol/nutrafol-ui-kit/dist/TextElementNova"
import { ButtonNova } from "@nutrafol/nutrafol-ui-kit/dist/ButtonNova"
import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"

import UserContext from "../../../../context/UserContext"

import Section from "../section/section"
import Tag from "../tag/tag"
import LoadingAnimation from "../../../loading/loadinganimation"

import {
  Wrapper,
  Banner,
  FloatWrapper,
  Tooltip,
} from "./quizresurfacebanner.styled"

import { readCookie, deleteCookie } from "../../../../utils/cookies"

import InfoIcon from "../../../../assets/icons/wild/info-icon.svg"

const mageURL = process.env.GATSBY_MAGENTO_API_URL
const mageProtocol = process.env.GATSBY_MAGENTO_API_PROTOCOL
const bearerToken = process.env.GATSBY_MAGENTO_QUIZ_RESULTS_API_BEARER_TOKEN
const token = readCookie("resurface-quiz-token")
const resultsUrl = `${mageProtocol}://${mageURL}/rest/V1/quiz/results/${token}/${process.env.GATSBY_QUIZ_VERSION}/1`

const coreCategories = {
  WOM: "Women",
  BAL: "Balance",
  VEG: "Vegan",
  POS: "Postpartum",
}

const coreSkus = ["WOM1", "BAL1", "VEG1", "POS1"]

const QuizResurfaceBanner = () => {
  const { isLoggedIn } = useContext(UserContext)

  const [productData, setProductData] = useState(null)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [loading, setLoading] = useState(true)
  const [showTooltip, setShowTooltip] = useState(false)
  const tooltipRef = useRef(null)
  const initialClickRef = useRef(false)

  useEffect(() => {
    if (!isCollapsed && !productData) {
      const fetchResults = async () => {
        const response = await fetch(resultsUrl, {
          method: "GET",
          withCredentials: true,
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${bearerToken}`,
          },
        })

        const data = await response.json()
        const results = JSON.parse(data)
        const resultsCombos = Object.keys(results.combinations)
        const productRecs = resultsCombos[resultsCombos.length - 1]
        const resultsData = {
          ...results.combinations[productRecs],
          description: Object.values(results?.products).at(-1)?.description,
        }

        setProductData(resultsData)
        setLoading(false)
      }

      fetchResults()
    }
  }, [isCollapsed, productData])

  useEffect(() => {
    const handleHideTooltip = (e) => {
      if (!initialClickRef.current) {
        if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
          setShowTooltip(false)
        }
      }
    }

    if (showTooltip) {
      initialClickRef.current = true
      setTimeout(() => {
        initialClickRef.current = false
      }, 0)
      document.addEventListener("click", handleHideTooltip)
    } else {
      document.removeEventListener("click", handleHideTooltip)
    }

    return () => {
      document.removeEventListener("click", handleHideTooltip)
    }
  }, [showTooltip])
  const showQuizBanner = () => {
    segmentEvent("Banner Expanded", {
      name: "Quiz Product Recommendation",
    })
    setIsCollapsed(false)
  }

  const hideQuizBanner = () => {
    segmentEvent("Banner Closed", {
      name: "Quiz Product Recommendation",
    })
    setIsCollapsed(true)
  }

  const handleShowTooltip = () => {
    setShowTooltip(true)
  }

  const handleAtcClick = (sku = `${productData?.sku}-S01`) => {
    segmentEvent("Product Added", {
      billing_interval: "1 month",
      name: productData?.name,
      price: parseInt(productData?.bundled_price.replace(".00", "")),
      product_category: "Growthplan",
      purchase_type: "Growthplan 1",
      quantity: 1,
      quiz_reco: true,
      product_gender: "Women",
      shipping_interval: "1 month",
      sku: sku,
      core_category: coreCategories[sku.slice(0, 3)],
      source: "quiz_banner",
      text: "Subscribe",
      type: "button",
      variant: "Monthly Delivery",
      business_line: "Hair",
    })

    // delete token cookie to not show banner again
    deleteCookie("resurface-quiz-token")

    // add to cart
    const url =
      process.env.GATSBY_MAGENTO_URL +
      "/dpa/add/tocart/?skus=" +
      sku +
      "&purchase_source=quiz_banner"
    window.location.href = url
  }

  if (!token) return null

  return (
    <Wrapper className={isLoggedIn ? "logged-in" : ""}>
      <div className="section-wrapper">
        <Banner>
          <TextElementNova
            text="Your personalized routine <span class='whitespace-nowrap font-grilliBook'>is waiting.</span>"
            className="Title-10-Super-Normal text-center mb-1 text-nova-brand-primary-100"
          />
          <button
            className="block mx-auto revisit-cta"
            onClick={showQuizBanner}
          >
            <TextElementNova
              text="Revisit your quiz results&rarr;"
              className="Body-10-Dia-Strong text-nova-brand-primary-100"
            />
          </button>
        </Banner>
      </div>
      {!isCollapsed ? (
        <FloatWrapper
          className={`section-wrapper ${isLoggedIn ? "logged-in" : ""}`}
        >
          <Section px={[16, 16, 80]} py={[16, 16, 48, 80]} className="relative">
            <div className="mb-4 sm:mb-0 sm:absolute sm:top-8 sm:right-8 md:top-16 md:right-[171px]">
              <ButtonNova
                label={"Close"}
                level={"Textlink"}
                className="ml-auto"
                onClick={hideQuizBanner}
              />
            </div>
            <div className="pb-4 mb-4 sm:pb-8 sm:mb-8 border-b sm:px-8 border-nova-transparency-black-20">
              <TextElementNova
                text="Your Product Recommendations"
                className="Title-20-Super-Normal text-nova-brand-primary-100 mb-2"
              />
              <div className="flex gap-1 items-center">
                <TextElementNova
                  text="Here’s a hair growth routine tailored to your specific needs."
                  className="Body-30-Dia-Normal"
                />
                <div ref={tooltipRef} className="relative h-4 w-4">
                  <button onClick={handleShowTooltip}>
                    <InfoIcon width="16" height="16" />
                  </button>
                  {showTooltip ? (
                    <Tooltip>
                      <TextElementNova
                        text="Results are based on your responses to the quiz."
                        className="Body-10-Dia-Normal"
                      />
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            </div>
            {loading ? <LoadingAnimation /> : null}
            <div
              className={`flex flex-col sm:flex-row lg:px-8 gap-4 ${
                loading ? "invisible" : ""
              }`}
            >
              <div className="flex-1 flex gap-4 flex-wrap">
                <div className="w-[120px] sm:w-[104px] lg:w-[175px]">
                  <img
                    src={
                      coreSkus.includes(productData?.sku)
                        ? productData?.label3
                        : productData?.label4
                    }
                    alt=""
                  />
                </div>
                <div className="flex-1 justify-center flex flex-col">
                  <div className="mb-4 md:mb-3">
                    <TextElementNova
                      text={productData?.name}
                      className="Body-20-Dia-Strong"
                    />
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <TextElementNova
                      text={`$${productData?.price.replace(".00", "")}`}
                      className="Body-10-Dia-Normal text-nova-transparency-black-60 line-through"
                    />
                    <TextElementNova
                      text={`$${productData?.bundled_price.replace(
                        ".00",
                        ""
                      )}/mo`}
                      className="Body-20-Dia-Strong"
                    />
                    <Tag
                      data={{
                        line: "Save 10%",
                        classes: "bg-nova-brand-accent-80",
                      }}
                    />
                  </div>
                  <div className="w-full hidden md:block mt-4 md:mt-3">
                    <TextElementNova
                      text={productData?.description}
                      className="Body-20-Dia-Normal max-w-[343px]"
                    />
                  </div>
                </div>
                <div className="w-full block md:hidden">
                  <TextElementNova
                    text={productData?.description}
                    className="Body-20-Dia-Normal max-w-[343px]"
                  />
                </div>
              </div>
              <div className="w-full sm:w-[240px] md:w-[370px] lg:w-[483px]">
                <ButtonNova
                  label={"Subscribe"}
                  icon={"Right"}
                  width={"100%"}
                  className="mb-2 !p-3"
                  onClick={() => handleAtcClick()}
                />
                <TextElementNova
                  text="Free shipping. Pause or cancel anytime."
                  className="Caption-10-Dia-Normal text-nova-transparency-black-60 text-center mb-5"
                />
                <ButtonNova
                  label={`Buy Once for $${productData?.price.replace(
                    ".00",
                    ""
                  )} + $6.95 shipping`}
                  level={"Textlink"}
                  className="mx-auto mb-2"
                  onClick={() => {
                    handleAtcClick(`${productData?.sku}-N01`)
                  }}
                />
                <TextElementNova
                  text="We recommend 3-6 months of <span class='whitespace-nowrap font-dinamoRegular'>consistent use to see results.</span>"
                  className="Body-10-Dia-Normal text-nova-brand-primary-80 text-center"
                />
              </div>
            </div>
          </Section>
        </FloatWrapper>
      ) : null}
    </Wrapper>
  )
}

export default QuizResurfaceBanner
